import React from 'react';
import {useTranslation} from "react-i18next";
import {useFormikContext} from "formik";
import {Box, Checkbox, FormControlLabel, FormHelperText, InputAdornment, TextField} from "@mui/material";
import {Typography} from "@mui/joy";
import {
    ContaminationDeclarationFormValues,
    DECLARATION_ACCEPTED_FIELD,
    isTextField,
    isYesNoField,
    MULTILINE_TEXT_FIELDS,
    REASON_FOR_SERVICE_FIELD,
    RUNNING_TIME_HOURS_FIELD,
    SUBSTANCES_FIELD,
    TextField as TextFieldKey,
} from "../../utils/contaminationDeclaration/fields";
import {CatalogAttribute, getAttributeLabel} from "../../utils/contaminationDeclaration/fallbackCatalog";
import YesNoToggle from "./YesNoToggle";
import QuickPickChips from "./QuickPickChips";
import SubstancesTable from "./SubstancesTable";
import QuestionRow from "./QuestionRow";

// Fallback sentence when the catalog carries no `hint` for the consent box
// (prototype wording; the PDF's own text is authoritative).
export const LEGAL_DECLARATION_TEXT = 'I hereby declare that the information supplied on this form is complete and accurate, '
    + 'and that dispatch of the asset complies with the applicable regulations on packaging, transport and labeling of dangerous goods.';

/** Grid columns (of 12) a field takes on `md` and up; the form wraps fields in Grid items. */
export const getFieldColumns = (attribute: CatalogAttribute): 6 | 12 => {
    const index = attribute.index;
    if (index === SUBSTANCES_FIELD || isYesNoField(index) || index === DECLARATION_ACCEPTED_FIELD) return 12;
    if (index === RUNNING_TIME_HOURS_FIELD) return 6;
    if (isTextField(index)) return MULTILINE_TEXT_FIELDS.includes(index) ? 12 : 6;
    return 6;
};

interface Props {
    attribute: CatalogAttribute;
    /** Required and still unanswered — shown as a hint once the user tried to save. */
    missing?: boolean;
    disabled?: boolean;
    /** Safety notice for this question (server warning or live mirror). */
    warning?: string | null;
    /** Title of the surrounding section (to avoid repeating it as a field label). */
    sectionTitle?: string;
    /**
     * `row`: short text answers render as a question-list row (label left,
     * input right) so they line up with neighbouring yes/no questions.
     * Default `grid`: TDAForm-style labelled field.
     */
    layout?: 'grid' | 'row';
}

/**
 * Renders one catalog question (content only — the form places it in the
 * grid). Renderer is chosen by the field's role (yes/no, running time, reason
 * with quick picks, substances table, consent box, free text); unknown catalog
 * additions render read-only so nothing is silently dropped. Inputs follow
 * TDAForm's stock look (small outlined TextField with shrunk label).
 */
const FieldRenderer = ({attribute, missing, disabled, warning, sectionTitle, layout = 'grid'}: Props) => {
    const {t} = useTranslation();
    // Errors are shown regardless of touch state: the diff-based save validates on submit only.
    const {values, initialValues, errors, handleChange, handleBlur, setFieldValue} =
        useFormikContext<ContaminationDeclarationFormValues>();
    const index = attribute.index;
    const label = getAttributeLabel(attribute);

    const fieldError = (errors as Record<string, string | undefined>)[index];
    const errorText = fieldError ? translateFieldError(fieldError, t) : undefined;

    if (index === SUBSTANCES_FIELD) {
        return (
            <Box>
                {/* The catalog's name usually equals the section title — no need to repeat it. */}
                {attribute.name && attribute.name !== sectionTitle && (
                    <Typography level="bodyBold" sx={{color: '#333', mb: 1}}>{label}</Typography>
                )}
                <SubstancesTable disabled={disabled}/>
            </Box>
        );
    }

    if (isYesNoField(index)) {
        return (
            <YesNoToggle index={index} label={label} hint={attribute.hint} missing={missing} disabled={disabled} warning={warning}/>
        );
    }

    if (index === DECLARATION_ACCEPTED_FIELD) {
        // Unticked by the re-confirmation rule (saved as accepted, answers edited since).
        const reconfirm = initialValues.declarationAccepted && !values.declarationAccepted;
        const consentHint = reconfirm
            ? t('The answers were changed – please confirm the declaration again.')
            : missing && !values.declarationAccepted ? t('Not accepted yet') : null;
        return (
            <FormControlLabel
                sx={{alignItems: 'flex-start', ml: 0}}
                control={
                    <Checkbox
                        checked={values.declarationAccepted}
                        onChange={(e) => setFieldValue(DECLARATION_ACCEPTED_FIELD, e.target.checked)}
                        disabled={disabled}
                        sx={{mt: '-6px'}}
                        inputProps={{'data-testid': 'doc-accept'} as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                }
                label={
                    <Box>
                        {/* The catalog's name usually equals the section title — then the sentence alone is the label. */}
                        {attribute.name && attribute.name !== sectionTitle && (
                            <Typography level="bodyBold" sx={{color: '#333'}}>{label}</Typography>
                        )}
                        <Typography level="body-md" sx={{color: '#333'}}>{attribute.hint || LEGAL_DECLARATION_TEXT}</Typography>
                        {/* Material helper text (like the TextFields' hints) — a Joy color prop is
                            overridden inside FormControlLabel's label typography. */}
                        {consentHint && (
                            <FormHelperText sx={{color: 'warning.main', mx: 0}} data-testid="doc-missing-hint">
                                {consentHint}
                            </FormHelperText>
                        )}
                    </Box>
                }
            />
        );
    }

    if (index === RUNNING_TIME_HOURS_FIELD) {
        return (
            <TextField
                id={index}
                name={index}
                fullWidth
                size="small"
                label={label}
                value={values.runningTimeHours}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
                error={Boolean(errorText)}
                helperText={errorText || attribute.hint || undefined}
                inputProps={{inputMode: 'numeric', 'data-testid': 'doc-field-runningTimeHours'}}
                InputProps={{endAdornment: <InputAdornment position="end">h</InputAdornment>}}
                InputLabelProps={{shrink: true}}
            />
        );
    }

    if (isTextField(index)) {
        const multiline = MULTILINE_TEXT_FIELDS.includes(index);
        const value = values[index as TextFieldKey] ?? '';
        const showMissing = missing && value.trim() === '';

        if (layout === 'row' && !multiline) {
            return (
                <QuestionRow
                    label={label}
                    hint={attribute.hint}
                    missing={showMissing}
                    testId={`doc-row-${index}`}
                    control={
                        <TextField
                            id={index}
                            name={index}
                            size="small"
                            value={value}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={disabled}
                            error={Boolean(errorText)}
                            helperText={errorText || undefined}
                            inputProps={{'data-testid': `doc-field-${index}`, 'aria-label': label}}
                            sx={{width: {xs: '100%', sm: 280}}}
                        />
                    }
                />
            );
        }

        return (
            <Box>
                <TextField
                    id={index}
                    name={index}
                    fullWidth
                    size="small"
                    label={label}
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={disabled}
                    multiline={multiline}
                    minRows={multiline ? 3 : undefined}
                    error={Boolean(errorText)}
                    helperText={errorText || (showMissing ? t('Not answered yet') : attribute.hint) || undefined}
                    FormHelperTextProps={showMissing && !errorText ? {sx: {color: 'warning.main'}} : undefined}
                    inputProps={{'data-testid': `doc-field-${index}`}}
                    InputLabelProps={{shrink: true}}
                />
                {index === REASON_FOR_SERVICE_FIELD && (
                    <QuickPickChips
                        options={attribute.optionValues}
                        value={value}
                        disabled={disabled}
                        onPick={(picked) => setFieldValue(index, picked)}
                        testId="doc-reason-quick-pick"
                    />
                )}
            </Box>
        );
    }

    // Unknown catalog addition: show its stored value read-only instead of hiding it.
    const raw = attribute.value;
    const display = raw === null || raw === undefined ? '' : typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
    return (
        <TextField fullWidth size="small" label={label} value={display} disabled InputLabelProps={{shrink: true}}/>
    );
};

export const translateFieldError = (code: string, t: (key: string) => string): string => {
    switch (code) {
        case 'INVALID_NUMBER':
            return t('Please enter a whole number of hours, e.g. 8140.');
        case 'REQUIRED':
            return t('Required when "other harmful substances" is answered with Yes.');
        default:
            return code;
    }
};

export default FieldRenderer;
